/**
 * Text Tools API Service
 */
import { apiClient } from '@/lib/api-client';

const BASE = '/api/v2/text-tools';

export interface TextToolInfo {
  code: string;
  name: string;
  description: string;
  base_credits: number;
  tier: number;
  icon: string;
  model_multipliers: Record<string, number>;
}

export interface TextToolConfigResponse {
  code: string;
  name: string;
  description: string;
  feature_code: string;
  base_credits: number;
  model_multipliers: Record<string, number>;
  system_prompt: string;
  temperature: number;
  max_output_tokens: number;
  top_p: number;
  top_k: number;
  tier: number;
  icon: string;
  input_size_tiers: [number | null, number][];
  supports_vision: boolean;
}

export interface TextGenerateRequest {
  tool_code: string;
  prompt: string;
  system_prompt?: string;
  model?: string;
  preset_id?: string;
  template_id?: string;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  top_k?: number;
  use_cache?: boolean;
  json_mode?: boolean;
  google_search?: boolean;
  output_format?: string;
  writing_style?: string;
  natural_style?: boolean;
  target_language?: string;
  // Welle 1 Modifier
  text_style?: string;
  length_bucket?: string;
  reading_level?: string;
  goal?: string;
  // Welle 3 Multi-Modal (MVP: 1 Bild)
  image_key?: string;
  // Welle 4/6 Brand (Backend: prompt_builder injects the brand's ai_description)
  brand_id?: string;
  // Welle 5 Multi-Variant (1 = classic single path, 2-5 = N variants)
  n_variants?: number;
  // Phase 1+2 Produkt-Kontext (server-seitiges Prefill)
  product_id?: string;
  field_type?: string;
  channel_id?: string;
  // Systemvorlagen: explizit gewähltes GOKI-Tool (überschreibt feld-impliziter Tool)
  system_tool_code?: string;
}

export interface FieldMapEntry {
  field_type: string;
  tool_code: string;
  vision: boolean;
}

export interface ApplyProductFieldRequest {
  product_id: string;
  field_type: string;
  content: string;
  source_generation_id?: string;
  channel_id?: string;
}

export interface ApplyProductFieldResponse {
  success: boolean;
  product_id: string;
  field_type: string;
  previous_value: string | null;
}

export type RefineIntent =
  | 'shorter'
  | 'longer'
  | 'casual'
  | 'formal'
  | 'with_hashtags'
  | 'new_hook';

export interface RefineTextRequest {
  tool_code: string;
  previous_output: string;
  refine_intent: RefineIntent;
  model?: string;
  parent_event_id?: string;
}

export interface TextGenerateResponse {
  success: boolean;
  content: string;
  provider: string;
  model: string;
  tool_code: string;
  credits_charged: number;
  base_credits: number;
  model_multiplier: number;
  input_multiplier: number;
  token_usage: {
    input_tokens: number;
    output_tokens: number;
    cached_tokens: number;
    total_tokens: number;
  };
  metadata: Record<string, unknown>;
  error?: string;
  // Welle 5 Multi-Variant — empty on the classic single path
  variants?: TextGenerateResponse[];
  failed_count?: number;
  refunded_credits?: number;
}

export interface TextPreset {
  id: string;
  name: string;
  system_prompt: string;
  user_prompt: string;
  tool_code: string | null;
  is_public: boolean;
  settings?: Record<string, unknown>;
}

export interface TextTemplate {
  id: string;
  workspace_id: string;
  user_id: string;
  name: string;
  tool_code: string | null;
  system_prompt: string;
  user_prompt: string;
  settings: Record<string, unknown>;
  is_public: boolean;
  template_type: string;
  created_at: string;
  updated_at: string | null;
}

export interface TextTemplatePayload {
  name: string;
  tool_code: string;
  system_prompt?: string;
  user_prompt?: string;
  settings?: Record<string, unknown>;
}

export async function fetchTextTools(): Promise<TextToolInfo[]> {
  const resp = await apiClient.get<{ tools: TextToolInfo[] }>(`${BASE}/tools`);
  return resp.data.tools;
}

export async function fetchToolConfig(toolCode: string): Promise<TextToolConfigResponse> {
  const resp = await apiClient.get<{ config: TextToolConfigResponse }>(`${BASE}/tools/${toolCode}/config`);
  return resp.data.config;
}

export async function fetchToolPresets(toolCode: string): Promise<TextPreset[]> {
  const resp = await apiClient.get<{ presets: TextPreset[] }>(`${BASE}/tools/${toolCode}/presets`);
  return resp.data.presets;
}

export async function fetchFieldMap(): Promise<FieldMapEntry[]> {
  const resp = await apiClient.get<{ field_map: FieldMapEntry[] }>(`${BASE}/field-map`);
  return resp.data.field_map;
}

export async function applyProductField(
  request: ApplyProductFieldRequest,
): Promise<ApplyProductFieldResponse> {
  const resp = await apiClient.post<ApplyProductFieldResponse>(
    `${BASE}/apply-product-field`,
    request,
  );
  return resp.data;
}

export async function generateText(request: TextGenerateRequest): Promise<TextGenerateResponse> {
  const resp = await apiClient.post<TextGenerateResponse>(`${BASE}/generate`, request);
  return resp.data;
}

export async function refineText(request: RefineTextRequest): Promise<TextGenerateResponse> {
  const resp = await apiClient.post<TextGenerateResponse>(`${BASE}/refine`, request);
  return resp.data;
}

// =============================================================================
// Workspace Templates
// =============================================================================

export async function listWorkspaceTemplates(toolCode?: string): Promise<TextTemplate[]> {
  const url = toolCode ? `${BASE}/templates?tool_code=${encodeURIComponent(toolCode)}` : `${BASE}/templates`;
  const resp = await apiClient.get<{ templates: TextTemplate[] }>(url);
  return resp.data.templates;
}

/**
 * Fetch ALL workspace templates (without tool_code filter).
 * Used by the Text Tools Hub "Meine Vorlagen" section and the
 * dedicated Vorlage detail page (which finds its template by id).
 * Backend returns every template the workspace owns regardless of tool_code.
 */
export async function fetchAllWorkspaceTextTemplates(): Promise<TextTemplate[]> {
  const resp = await apiClient.get<{ templates: TextTemplate[] }>(`${BASE}/templates`);
  return resp.data.templates;
}

export async function createTemplate(payload: TextTemplatePayload): Promise<TextTemplate> {
  const resp = await apiClient.post<{ template: TextTemplate }>(`${BASE}/templates`, payload);
  return resp.data.template;
}

export async function updateTemplate(
  id: string,
  payload: Partial<TextTemplatePayload>,
): Promise<TextTemplate> {
  const resp = await apiClient.put<{ template: TextTemplate }>(`${BASE}/templates/${id}`, payload);
  return resp.data.template;
}

export async function deleteTemplate(id: string): Promise<{ success: boolean }> {
  const resp = await apiClient.delete<{ success: boolean }>(`${BASE}/templates/${id}`);
  return resp.data;
}
